import { useState, useEffect, useMemo } from 'react';
import { dashboardAPI } from '../lib/api';
import { getCurrencySymbol } from '../lib/currencies';
import { useProperty } from '../contexts/PropertyContext';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, getDay } from 'date-fns';
import { TrendingUp, CalendarDays, Bell, ChevronRight, Loader2 } from 'lucide-react';

/* ═══════════════════ 1. Sales Analytics Chart ═══════════════════ */

function SalesChart({ data, currentYear, prevYear, currency }) {
  const sym = getCurrencySymbol(currency || 'USD');

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white shadow-lg border border-gray-200 rounded-lg px-3 py-2 text-xs">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-gray-500">{currentYear}:</span>
          <span className="font-bold text-gray-900">{sym}{payload[0]?.value?.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-gray-300" />
          <span className="text-gray-500">{prevYear}:</span>
          <span className="font-medium text-gray-500">{sym}{payload[1]?.value?.toLocaleString()}</span>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -15, bottom: -5 }}>
        <defs>
          <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.15} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={40} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="current_year" stroke="#3b82f6" strokeWidth={2.5} fill="url(#salesGrad)" dot={false} activeDot={{ r: 4, fill: '#3b82f6' }} />
        <Area type="monotone" dataKey="previous_year" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="5 5" fill="none" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function SalesAnalyticsCard() {
  const { selectedProperty, properties } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await dashboardAPI.salesAnalytics(selectedProperty || undefined);
        setData(res.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, [selectedProperty]);

  const currency = useMemo(() => {
    if (!selectedProperty || !properties?.length) return 'USD';
    const p = properties.find(x => x.id === selectedProperty);
    return p?.currency || 'USD';
  }, [selectedProperty, properties]);

  const sym = getCurrencySymbol(currency);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="sales-analytics-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-blue-500" />
          <h3 className="text-sm font-semibold text-gray-900">Sales Analytics</h3>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded bg-blue-500" /><span className="text-gray-500">{data.current_year}</span><span className="font-bold text-gray-900">{sym}{data.current_year_total?.toLocaleString()}</span></div>
            <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded bg-gray-300 border-dashed" style={{borderTop: '1px dashed #cbd5e1'}} /><span className="text-gray-400">{data.previous_year}</span><span className="font-medium text-gray-400">{sym}{data.previous_year_total?.toLocaleString()}</span></div>
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-[200px]"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
      ) : !data ? (
        <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">No sales data</div>
      ) : (
        <SalesChart data={data.months} currentYear={data.current_year} prevYear={data.previous_year} currency={currency} />
      )}
    </div>
  );
}

/* ═══════════════════ 2. Rate Calendar ═══════════════════ */

export function RateCalendarCard() {
  const { selectedProperty, properties } = useProperty();
  const [calData, setCalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const currency = useMemo(() => {
    if (!selectedProperty || !properties?.length) return 'USD';
    const p = properties.find(x => x.id === selectedProperty);
    return p?.currency || 'USD';
  }, [selectedProperty, properties]);

  const sym = getCurrencySymbol(currency);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { calendarAPI } = await import('../lib/api');
        const month = format(now, 'yyyy-MM');
        const calRes = await calendarAPI.get(month, 1, selectedProperty || undefined);
        setCalData(calRes.data);
      } catch { setCalData(null); }
      finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  // Build a lookup of date -> rate from calendar data
  const rateMap = useMemo(() => {
    if (!calData?.months?.[0]?.days) return {};
    const map = {};
    for (const d of calData.months[0].days) {
      map[d.date] = { rate: Math.round(d.rate || 0), bookings: d.bookings || 0, demand: d.demand };
    }
    return map;
  }, [calData]);

  // Pad start of month to align with weekday
  const firstDayOfWeek = getDay(monthStart); // 0=Sun
  const padStart = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Mon=0

  const demandColor = (demand) => {
    switch (demand) {
      case 'high': case 'unavailable': return 'bg-red-50 text-red-600 border-red-200';
      case 'good': return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'normal': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      default: return 'bg-gray-50 text-gray-500 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="rate-calendar-card">
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays className="h-4 w-4 text-emerald-500" />
        <h3 className="text-sm font-semibold text-gray-900">{format(now, 'MMMM yyyy')} — Rates</h3>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-[200px]"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
      ) : (
        <div>
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} className="text-center text-[10px] font-semibold text-gray-400 uppercase py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: padStart }).map((_, i) => <div key={`pad-${i}`} />)}
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const info = rateMap[dateStr];
              const rate = info?.rate || 0;
              const today = isToday(day);
              const colors = info ? demandColor(info.demand) : 'bg-gray-50 text-gray-400 border-gray-100';

              return (
                <div
                  key={dateStr}
                  className={`relative rounded-md border p-1 text-center transition-colors ${colors} ${today ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
                  title={`${dateStr}: ${sym}${rate} | ${info?.bookings || 0} booking(s)`}
                >
                  <p className={`text-[10px] leading-tight ${today ? 'font-bold text-blue-600' : 'font-medium'}`}>{format(day, 'd')}</p>
                  {rate > 0 && <p className="text-[9px] font-bold leading-tight mt-0.5">{sym}{rate}</p>}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-3 mt-3 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-gray-200" />Low</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-300" />Normal</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-300" />Good</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-300" />High</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ 3. Recent Reservations ═══════════════════ */

const SOURCE_BADGE = {
  'walk-in': { label: 'Walk-in', cls: 'bg-purple-100 text-purple-700' },
  'online': { label: 'Online', cls: 'bg-blue-100 text-blue-700' },
  'channex': { label: 'Channex', cls: 'bg-emerald-100 text-emerald-700' },
  'booking.com': { label: 'Booking.com', cls: 'bg-blue-100 text-blue-700' },
  'airbnb': { label: 'Airbnb', cls: 'bg-rose-100 text-rose-700' },
  'expedia': { label: 'Expedia', cls: 'bg-yellow-100 text-yellow-700' },
};

export function RecentReservationsCard({ onBookingClick }) {
  const { selectedProperty, properties } = useProperty();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const currency = useMemo(() => {
    if (!selectedProperty || !properties?.length) return 'USD';
    const p = properties.find(x => x.id === selectedProperty);
    return p?.currency || 'USD';
  }, [selectedProperty, properties]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await dashboardAPI.recentReservations(selectedProperty || undefined, 8);
        setReservations(res.data.reservations || []);
      } catch { setReservations([]); }
      finally { setLoading(false); }
    })();
  }, [selectedProperty]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="recent-reservations-card">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-gray-900">Recent Reservations</h3>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-[200px]"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
      ) : reservations.length === 0 ? (
        <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">No reservations yet</div>
      ) : (
        <div className="space-y-0 max-h-[240px] overflow-y-auto">
          {reservations.map(r => {
            const src = SOURCE_BADGE[r.source || r.ota_name] || SOURCE_BADGE[r.ota_name] || { label: r.source || r.ota_name || 'Direct', cls: 'bg-gray-100 text-gray-600' };
            const sym = getCurrencySymbol(r.currency || currency);
            return (
              <button
                key={r.id}
                onClick={() => onBookingClick?.(r)}
                className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                data-testid={`recent-reservation-${r.id}`}
              >
                <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${src.cls}`}>{src.label}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.guest_name}</p>
                  <p className="text-[11px] text-gray-400">{r.check_in} &middot; {r.nights || '?'}N</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-sm font-bold text-gray-900">{sym}{(r.total_amount || 0).toLocaleString()}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
